import React, { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import './TrialClassForm.css';

interface TrialFormData {
    firstName: string;
    surname: string;
    address: string;
    postCode: string;
    dateOfBirth: string;
    height: string;
    primaryEmail: string;
    secondaryEmail: string;
    primaryPhone: string;
    secondaryPhone: string;
    medicalConditions: string;
    whereDidYouFindUs: string;
}

const TrialClassForm: React.FC = () => {
    const [formData, setFormData] = useState<TrialFormData>({
        firstName: '',
        surname: '',
        address: '',
        postCode: '',
        dateOfBirth: '',
        height: '',
        primaryEmail: '',
        secondaryEmail: '',
        primaryPhone: '',
        secondaryPhone: '',
        medicalConditions: '',
        whereDidYouFindUs: ''
    });

    const [submitted, setSubmitted] = useState<boolean>(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        
        const encode = (data: any) => {
            return Object.keys(data)
                .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
                .join("&");
        };

        fetch("/", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: encode({ "form-name": "trial-class", ...formData })
        })
            .then(() => {
                setSubmitted(true);
                setFormData({
                    firstName: '',
                    surname: '',
                    address: '',
                    postCode: '',
                    dateOfBirth: '',
                    height: '',
                    primaryEmail: '',
                    secondaryEmail: '',
                    primaryPhone: '',
                    secondaryPhone: '',
                    medicalConditions: '',
                    whereDidYouFindUs: ''
                });
                setTimeout(() => {
                    setSubmitted(false);
                }, 5000);
            })
            .catch(error => console.error("Error submitting form:", error));
    };

    return (
        <section className="trial-class-section page-section">
            <div className="container">
                <h1 className="section-title">Trial / Joining Form</h1>
                <p className="section-subtitle">
                    Join us for a free trial and experience Taekwon-Do firsthand. Please fill out the form below to register.
                </p>

                <div className="trial-class-content">
                    <form 
                        className="trial-form" 
                        name="trial-class" 
                        method="post" 
                        data-netlify="true" 
                        data-netlify-honeypot="bot-field" 
                        onSubmit={handleSubmit}
                    >
                        <input type="hidden" name="form-name" value="trial-class" />
                        <div hidden>
                            <input name="bot-field" />
                        </div>
                        {submitted && (
                            <div className="success-message">
                                Thank you! Your registration has been received. We will contact you soon.
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="firstName">First Name *</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    placeholder="First Name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="surname">Surname *</label>
                                <input
                                    type="text"
                                    id="surname"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    required
                                    placeholder="Surname"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="address">Address *</label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                required
                                placeholder="Street Address"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="postCode">Post Code *</label>
                                <input
                                    type="text"
                                    id="postCode"
                                    name="postCode"
                                    value={formData.postCode}
                                    onChange={handleChange}
                                    required
                                    placeholder="Post Code"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="dateOfBirth">Date of Birth *</label>
                                <input
                                    type="date"
                                    id="dateOfBirth"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="height">Student Height *</label>
                                <input
                                    type="text"
                                    id="height"
                                    name="height"
                                    value={formData.height}
                                    onChange={handleChange}
                                    required
                                    placeholder="e.g. 5ft 4in / 163cm"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="primaryEmail">Primary Email *</label>
                                <input
                                    type="email"
                                    id="primaryEmail"
                                    name="primaryEmail"
                                    value={formData.primaryEmail}
                                    onChange={handleChange}
                                    required
                                    placeholder="Primary Email Address"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="secondaryEmail">Secondary Email</label>
                                <input
                                    type="email"
                                    id="secondaryEmail"
                                    name="secondaryEmail"
                                    value={formData.secondaryEmail}
                                    onChange={handleChange}
                                    placeholder="Secondary Email Address (Optional)"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="primaryPhone">Primary Contact Number *</label>
                                <input
                                    type="tel"
                                    id="primaryPhone"
                                    name="primaryPhone"
                                    value={formData.primaryPhone}
                                    onChange={handleChange}
                                    required
                                    placeholder="Primary Phone Number"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="secondaryPhone">Secondary Contact Number</label>
                                <input
                                    type="tel"
                                    id="secondaryPhone"
                                    name="secondaryPhone"
                                    value={formData.secondaryPhone}
                                    onChange={handleChange}
                                    placeholder="Secondary Phone Number (Optional)"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="medicalConditions">Any Medical / Neurological Conditions</label>
                            <textarea
                                id="medicalConditions"
                                name="medicalConditions"
                                value={formData.medicalConditions}
                                onChange={handleChange}
                                rows={4}
                                placeholder="Please let us know of any conditions we should be aware of (Optional)"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="whereDidYouFindUs">Where did you find us?</label>
                            <input
                                type="text"
                                id="whereDidYouFindUs"
                                name="whereDidYouFindUs"
                                value={formData.whereDidYouFindUs}
                                onChange={handleChange}
                                placeholder="e.g. Instagram, Google, a friend... (Optional)"
                            />
                        </div>

                        <button type="submit" className="submit-button">
                            Register for a Trial / Join here
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default TrialClassForm;
